import { useEffect, useState } from "react";
import LearnMore from "@/components/LearnMore";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import { Play, ArrowRight } from "lucide-react";

export default function Inner() {
  const [items, setItems] = useState([]);
  const [tonight, setTonight] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/installations").then((r) => setItems(r.data)).catch(() => {});
    api.get("/home").then((r) => setTonight(r.data.tonight)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Inner Architecture</p>
          <h1 className="font-serif text-4xl sm:text-5xl">Change what's underneath.</h1>
        <div className="mb-4"><LearnMore area="inner" /></div>
          <p className="text-muted-foreground mt-2 max-w-xl">Confidence isn't a script — it's a state. These installations rewire the beliefs beneath your words.</p>
        </motion.div>

        {tonight && (
          <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate(`/inner/${tonight.id}`)} data-testid="prescription-hero"
            className="group relative w-full text-left rounded-2xl overflow-hidden border border-border mb-12 min-h-[220px] flex">
            <img src={tonight.cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-500" />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/20" />
            <div className="relative p-8 flex flex-col justify-center max-w-lg">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">I want you to listen to this tonight</div>
              <h2 className="font-serif text-4xl mb-2">{tonight.title}</h2>
              <p className="text-sm text-muted-foreground mb-4">{tonight.why ? `${tonight.why} · ` : ""}{tonight.duration}</p>
              <span className="inline-flex items-center gap-2 text-primary font-medium"><span className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary transition-colors duration-300"><Play className="w-4 h-4 ml-0.5" fill="currentColor" /></span>Begin installation</span>
            </div>
          </motion.button>
        )}

        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Explore the library</span>
          <span className="flex-1 h-px bg-border" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 auto-rows-[240px]">
          {items.map((r, i) => (
            <motion.button key={r.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              onClick={() => navigate(`/inner/${r.id}`)} data-testid={`installation-${r.id}`}
              className={`group relative text-left rounded-2xl overflow-hidden border border-border transition-all duration-300 hover:-translate-y-1 ${i % 4 === 0 ? "md:col-span-2" : ""}`}>
              <img src={r.cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-55 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              <div className="relative h-full p-6 flex flex-col justify-between">
                <span className="text-xs uppercase tracking-[0.15em] text-primary self-start">{r.theme}</span>
                <div>
                  <h3 className="font-serif text-2xl mb-1">{r.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{r.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm text-primary"><span className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary transition-colors duration-300"><Play className="w-3.5 h-3.5 ml-0.5" fill="currentColor" /></span>{r.duration}</span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </main>
    </div>
  );
}
